import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import Header from '@components/DesignLibrary/Header';
import PageContext, { PageContextItems } from '@components/DesignLibrary/PageContext';
import DownloadArchive from '@components/Document/DownloadArchive';
import NoodleAI from '@components/Icons/NoodleAI';
import TrashSimple from '@components/Icons/TrashSimple';
import ProgressBarLoader from '@/components/ProgressBarLoader';
import Tooltip from '@components/Tooltip';
import DocumentRow from '@components/UserWorkflowInfo/DocumentsAction/DocumentRow';
import removeNullish from '@helpers/removeNullish';
import { IDENTIFIERS, getUrl } from '@helpers/urlsHelper';
import userWorkflowToContext from '@helpers/userWorkflowToContext';
import { usePrevious, useToast } from '@hooks';
import useNoodleApi from '@hooks/useNoodleApi';
import useLocalStorageStateSerializers from '@lib/UserWorkflowDetailCustomerView/useLocalStorageStateSerializers';
import { useIsPaneOpen, useOpenPacerPreviewPane, useOpenUserWorkflowDocumentPane, useOpenWorkflowDocumentsPane } from '@panes/hooks';
import { DocumentAccessContext, DocumentAccessProvider, PromptForDocumentAccessToken } from '@providers/DocumentAccess';
import { useLocalStorageState } from '@providers/LocalStorageState';
import * as tsClient from '@tsClient';
import { getUserWorkflowById } from '@tsClient/workflows';
import * as ApiModels from '@typings/api-models';
import { UserWorkflowCollaborator } from '@typings/api-models';
import { m } from 'framer-motion';
import Image from 'next/image';
import PACERLogo from '@lib/Creators/Dashboard/Integrations/assets/pacer.png';
import ECFManagerModal from '@/modals/ECFManagerModal';
import CollapsibleArea from '@/components/CollapsibleArea';
import CaretLeft from '@/components/Icons/CaretLeft';
import { Folder, FolderOpen } from '@phosphor-icons/react';
import pluralize from 'pluralize';
import { useFeatureFlag } from '@providers/FeatureFlags';
import SelectPetitionPdfDocumentsModal from '@/modals/SelectPetitionPdfDocumentsModal';
import Head from 'next/head';
import Button from '@/components/DesignLibrary/Button';
import FrameCorners from '@/components/Icons/FrameCorners';
import DownloadSimple from '@/components/Icons/DownloadSimple';
import DashboardContext from '@/layouts/DashboardLayout/DashboardContext';
import DocumentPreview from '@/lib/DocumentPreview/DocumentPreview';
import Buttons from '@/components/Buttons';
import downloadFilesAsZip from '@/helpers/downloadFilesAsZip';

import s from './UserWorkflowDocuments.module.scss';

type ThisCompiledDocument =
  & Pick<ApiModels.UserWorkflowDocument, 'id' | 'rank' | 'templateReferenceId' | 'referenceType' | 'path'>
  & Parameters<typeof DocumentRow>[0]['compiledDocument']
  & {
    isNew?: true;
    isEmptyRowUpload?: true;
    rejectedAt?: string | null;
    rejectionMessage?: string | null;
  };

type Props = {
  creatorId: string;
  userWorkflowId: string;
  isDownloadEnabled?: boolean;
  type?: 'modal' | 'sidepanel' | 'form-request' | 'page';
};

const UserWorkflowDocumentsWithFolders: FC<Props> = ({ userWorkflowId, creatorId, isDownloadEnabled, type }) => {
  const { accessToken } = useContext(DocumentAccessContext);
  const [compiledDocuments, setCompiledDocuments] = useState<ThisCompiledDocument[]>([]);
  const [documentOpen, setDocumentOpen] = useState<string | null>(null);
  const [folderedDocuments, setFolderedDocuments] = useState<Record<string, ThisCompiledDocument[]>>({});
  // @todo - better would be to parse the "folderedDocuments", but that is only partially built...
  const [documentsWithPaths, setDocumentsWithPaths] = useState<{ path: string[]; id: string }[]>([]);
  const containerRef = useRef<HTMLUListElement>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [petitionBuilderModalOpen, setPetitionBuilderModalOpen] = useState(false);
  const [downloadingFolder, setDownloadingFolder] = useState('');
  const [ecfManagerModalOpen, setEcfManagerModalOpen] = useState(false);
  const openWorkflowDocumentsPane = useOpenWorkflowDocumentsPane();
  const { isInDashboard } = useContext(DashboardContext);

  const [collaborators, setCollaborators] = useLocalStorageState<UserWorkflowCollaborator[]>({
    defaultValue: [],
    key: 'collaborators',
    prefix: 'workflows',
    ...useLocalStorageStateSerializers.collaborators,
  });

  const [context, setContext] = useState<PageContextItems>([]);
  const isUserWorkflowDocumentPaneOpen = useIsPaneOpen({
    type: 'userWorkflowDocument',
  });
  const wasUserWorkflowDocumentPaneOpen = usePrevious(isUserWorkflowDocumentPaneOpen);

  const openUserWorkflowDocumentPane = useOpenUserWorkflowDocumentPane();
  const openPacerPreviewPane = useOpenPacerPreviewPane();

  const {
    getData: generateUserWorkflowPdfs,
    fetchingState: { isFetching: isGeneratingPdfs },
  } = useNoodleApi(tsClient.workflows.generateUserWorkflowPdfs, {
    toastOnError: true,
  });
  const {
    data: loadedCompiledDocumentsResponse,
    fetchingState: { isFetching: compiledDocumentsFetching, isFetched: compiledDocumentsFetched },
    getData: getUserWorkflowDocuments,
  } = useNoodleApi(
    tsClient.workflows.getUserWorkflowCompiledDocuments,
    { toastOnError: true },
  );
  const { data: workflowData, getData: getWorkflow } = useNoodleApi(getUserWorkflowById, {
    toastOnError: true,
  });

  const { getData: updateUserWorkflowDocuments } = useNoodleApi(tsClient.workflows.updateUserWorkflowCompiledDocuments, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Documents saved.'],
  });
  const { data: loadedCollaborators, getData: getCollaboratorsFn } = useNoodleApi(tsClient.workflows.getUserWorkflowCollaborators, {
    toastOnError: true,
  });

  useEffect(() => {
    getUserWorkflowDocuments({ perPage: 1000, userWorkflowId });
  }, [userWorkflowId, getUserWorkflowDocuments]);

  useEffect(() => {
    if (workflowData) {
      (async () => {
        try {
          const userWorkflowContext = await userWorkflowToContext(workflowData);
          const combinedContext = [
            ...collaborators
              .map(c => {
                if (c.roleLabel && c.name && c.source === 'context-person') {
                  return {
                    id: c.id,
                    label: c.roleLabel,
                    value: c.name || '',
                  };
                }
                return null;
              })
              .filter(removeNullish),
            ...userWorkflowContext,
          ].filter(removeNullish);
          setContext(combinedContext);
        } catch (error) {
          console.error("Failed to load user workflow context", error);
        }
      })();
    }
  }, [workflowData, collaborators]);

  useEffect(() => {
    if (userWorkflowId) {
      getWorkflow({ userWorkflowId });
      getCollaboratorsFn({ userWorkflowId });
    }
  }, [userWorkflowId, getWorkflow, getCollaboratorsFn]);

  useEffect(() => {
    // Refetch documents when pane closes
    if (wasUserWorkflowDocumentPaneOpen && !isUserWorkflowDocumentPaneOpen) {
      getUserWorkflowDocuments({ perPage: 1000, userWorkflowId });
    }
  }, [userWorkflowId, isUserWorkflowDocumentPaneOpen, wasUserWorkflowDocumentPaneOpen, getUserWorkflowDocuments]);

  const refetchDocuments = useCallback(
    () => getUserWorkflowDocuments({ perPage: 1000, userWorkflowId }),
    [getUserWorkflowDocuments, userWorkflowId],
  );

  const isPacerEnabled = useFeatureFlag('pacer') && workflowData?.workflow.enablePacerSubmissions;

  useEffect(() => {
    setCompiledDocuments(
      loadedCompiledDocumentsResponse?.items.filter(
        doc =>
          doc.document?.fileName
          || ((doc.templateReferenceType === 'manually-added' || doc.templateReferenceType === 'document-request-file' || doc.templateReferenceType === 'ai-bot')
            && creatorId),
      ) || [],
    );
  }, [loadedCompiledDocumentsResponse, creatorId]);

  useEffect(() => {
    if (loadedCollaborators) {
      setCollaborators(loadedCollaborators);
    }
  }, [loadedCollaborators, setCollaborators]);

  useEffect(() => {
    if (compiledDocuments) {

      const folderedDocs = compiledDocuments.reduce((acc: Record<string, ThisCompiledDocument[]>, doc) => {
        const folderName = doc.path?.split('/')[0] || 'Other files';
        if (!acc[folderName]) {
          acc[folderName] = [];
        }
        acc[folderName].push(doc);
        return acc;
      }, {});

      setFolderedDocuments(folderedDocs);

      setDocumentsWithPaths(compiledDocuments
        .map(compiledDocument => {
          if (!compiledDocument.document) {
            return null;
          }
          const folderName = compiledDocument.path?.split('/')[0] || 'Other files';
          const path = (compiledDocument.referenceType === 'questionnaire-pdf-fill' || compiledDocument.referenceType === 'anvil' || compiledDocument.referenceType === 'custom-terms')
            ? [folderName]
            : [
              folderName,
              compiledDocument.title || 'Untitled',
            ];
          return {
            id: compiledDocument.document.id,
            path,
          };
        })
        .filter(removeNullish),
      );

    }
  }, [compiledDocuments]);

  const handleSave = useCallback(async (): Promise<void> => {
    const updates = compiledDocuments.map((doc, index) => ({
      ...doc,
      rank: index + 1,
    }))
      .map((doc, index) => {
        if (doc.isEmptyRowUpload && doc.document) {
          return {
            documentId: doc.document.id,
            id: doc.id,
            rank: index + 1,
            title: doc.title,
          };
        }
        if (!doc.isNew || !doc.document) {
          return {
            id: doc.id,
            rank: index + 1,
            title: doc.title,
          };
        }
        return {
          documentId: doc.document.id,
          rank: index + 1,
          title: doc.title,
        };
      })
      .filter(removeNullish);

    await updateUserWorkflowDocuments({
      updates,
      userWorkflowId,
    });
  }, [compiledDocuments, updateUserWorkflowDocuments, userWorkflowId]);

  const handleTitleChange = (documentId: string, newTitle: string): void => {
    setCompiledDocuments(items =>
      items.map(item => {
        if (item.id === documentId) {
          return {
            ...item,
            title: newTitle,
          };
        }
        return item;
      }),
    );
    setHasUnsavedChanges(true);
  };

  const handleRemoveItem = (documentId: string): void => {
    if (window.confirm('Are you sure you want to remove this document?')) {
      setCompiledDocuments(items => items.filter(item => item.id !== documentId));
      setHasUnsavedChanges(true);
    }
  };

  const handleEmptyRowManualUpload = (
    compiledDocument: ThisCompiledDocument,
    documents: Array<Pick<NonNullable<ThisCompiledDocument['document']>, 'id' | 'mimeType' | 'fileName' | 'fileSize'> >| null,
  ): void => {
    if (documents) {
      documents.forEach(document => {
        setCompiledDocuments(items => {
          const updatedItems = items.map(item =>
            item.id === compiledDocument.id
              ? ({
                ...item,
                document,
                isEmptyRowUpload: true,
                referenceId: document.id,
                referenceType: 'manually-added',
              } as ThisCompiledDocument)
              : item,
          );
          return updatedItems;
        });
      });
    }
    setHasUnsavedChanges(true);
  };

  useEffect(() => {
    const saveData = async (): Promise<void> => {
      await handleSave();
    };
    if (hasUnsavedChanges) {
      saveData();
      setHasUnsavedChanges(false);
    }
  }, [hasUnsavedChanges, handleSave]);

  const showDownloadLink = isDownloadEnabled && accessToken && isInDashboard;
  const showPromptForAccessToken = isDownloadEnabled && !accessToken && isInDashboard;

  const showGeneratePdfsButton = Boolean(workflowData?.workflow?.hasGeneratedPdfs);

  const handleGeneratePdfs = async (): Promise<void> => {
    if (showGeneratePdfsButton) {
      await generateUserWorkflowPdfs({ userWorkflowId });
      await getUserWorkflowDocuments({ perPage: 1000, userWorkflowId });
    }
  };

  if (!workflowData || !compiledDocuments || compiledDocumentsFetching) {
    return (
      <ProgressBarLoader
        chunks={[
          {
            label: "Loading compiled documents...",
            loaded: compiledDocuments && compiledDocumentsFetched,
          },
          {
            label: "Fetching case data...",
            loaded: Boolean(workflowData),
          },
        ]}
      />
    );
  }

  if (showPromptForAccessToken && isInDashboard) {
    return <PromptForDocumentAccessToken size="lg" className={s.passcode} label="Enter passcode to access case documents" />;
  }

  if (documentOpen) {
    return <>
      <Buttons isSmall onClick={() => setDocumentOpen(null)} className={s.documentsBackButton} iconBefore={<CaretLeft />}>
        Back to documents
      </Buttons>
      <DocumentAccessProvider>
        <DocumentPreview documentId={documentOpen} isModal />
      </DocumentAccessProvider>
    </>;
  }

  const pacerDocs = compiledDocuments.filter(doc => doc.integrationSlug === 'pacer');

  return (
    <>
      {!type && <Head>
        <title>{workflowData.person.name} Documents · Glade Dashboard</title>
      </Head>}
      <div className={s['compiled-documents-content']}>
        {(!type || type === 'page') && <>
          <Header
            title={workflowData.person.name}
            description={workflowData.product?.title}
            breadcrumbs={type === 'page'
              ? [
                {
                  href: getUrl(IDENTIFIERS.DASHBOARD_USER_WORKFLOW_DETAIL, { userWorkflowId }),
                  label: 'Go back to workflow',
                },
              ]
              : undefined}
            actions={[
              showGeneratePdfsButton
                ? {
                  isFetching: isGeneratingPdfs,
                  isPrimary: true,
                  label: 'Generate PDFs',
                  onClick: handleGeneratePdfs,
                }
                : null,
              isPacerEnabled
                ? {
                  componentBefore: <Image src={PACERLogo} alt="Download" width={16} height={16} />,
                  isSecondary: true,
                  label: 'ECF Manager',
                  onClick: () => setPetitionBuilderModalOpen(true),
                }
                : null,
              {
                href: getUrl(IDENTIFIERS.DASHBOARD_MEMBER, { id: workflowData.person.id }),
                label: 'Show Profile',
              },
            ].filter(removeNullish)}
          />
          <PageContext
            items={context}
          />
        </>}
        <ul ref={containerRef} className={s.table}>
          {Object.entries(folderedDocuments).length === 0 ? <p className="body-sm" style={{ padding: 16 }}>No documents yet.</p> : <></>}
          {Object.entries(folderedDocuments).map(([folder, documents], idx) => (
            <div key={folder} className={s.folder}>
              {accessToken && <Button
                size="xs"
                variant="secondary"
                loading={downloadingFolder === folder}
                className={s.downloadButton}
                onClick={async () => {
                  setDownloadingFolder(folder);
                  await downloadFilesAsZip(
                    documents
                      .filter((doc): doc is ThisCompiledDocument & { document: NonNullable<ThisCompiledDocument['document']> } => Boolean(doc.document))
                      .map(doc => ({
                        fileName: `${documentsWithPaths.find(d => d.id === doc.document.id)?.path.join("/")}/${doc.document.fileName}`,
                        id: doc.document.id,
                      })),
                    accessToken,
                    folder,
                  );
                  setDownloadingFolder('');
                }}
              >
                {type ? <>{downloadingFolder === folder ? 'Downloading' : <DownloadSimple />}</> : 'Download'}
              </Button>}
              <CollapsibleArea
                Icon={{ Component: Folder, ComponentOpen: FolderOpen }}
                title={folder}
                description={`${documents.filter(d => d.document).length} ${pluralize('item', documents.filter(d => d.document).length)}`}
                hideCaret
                clipTitle
                open={idx === 0 && type !== 'sidepanel'}
              >
                {(folder === 'Forms & Schedules' || folder === 'Terms & Agreements' || folder === 'Credit Report Document')
                  ? (
                    <ul>
                      {documents.map((doc) => (
                        <m.li key={doc.id} layout="position">
                          <div className={s.formsItem}>
                            <DocumentRow
                              creatorId={creatorId}
                              compiledDocument={doc}
                              onChangeTitle={handleTitleChange}
                              onUploadComplete={d => handleEmptyRowManualUpload(doc, d)}
                              fileNameOnly
                              viewInModal={type === 'modal'}
                              isViewOnly={!isInDashboard}
                              viewAction={type === 'form-request' ? setDocumentOpen : undefined}
                            />
                            {isInDashboard && <button className={s['circle-button']} onClick={() => handleRemoveItem(doc.id)}>
                              <TrashSimple size={16} weight="fill" color="var(--color-error)" />
                            </button>}
                          </div>
                        </m.li>
                      ))}
                    </ul>
                  )
                  : (
                    <div className={s.subfolders}>
                      {Object.entries(
                        documents.reduce((acc: Record<string, ThisCompiledDocument[]>, doc) => {
                          const title = doc.title || 'Untitled';
                          if (!acc[title]) {
                            acc[title] = [];
                          }
                          acc[title].push(doc);
                          return acc;
                        }, {}),
                      ).map(([title, titledDocuments]) => (
                        <div key={title} className={s.subfolder}>
                          {accessToken && <Button
                            loading={downloadingFolder === title}
                            variant='secondary'
                            size='xs'
                            className={s.downloadButton}
                            onClick={async () => {
                              setDownloadingFolder(title);
                              await downloadFilesAsZip(
                                titledDocuments
                                  .filter((d): d is ThisCompiledDocument & { document: NonNullable<ThisCompiledDocument['document']> } => Boolean(d.document))
                                  .map(d => ({
                                    fileName: d.document.fileName,
                                    id: d.document.id,
                                  })),
                                accessToken,
                                title,
                              );
                              setDownloadingFolder('');
                            }}>
                            {type ? <>{downloadingFolder === title ? 'Downloading' : <DownloadSimple />}</> : 'Download'}
                          </Button>}
                          <CollapsibleArea
                            Icon={{ Component: Folder, ComponentOpen: FolderOpen }}
                            title={title}
                            description={`${titledDocuments.filter(d => d.document).length} ${pluralize('item', titledDocuments.filter(d => d.document).length)}`}
                            hideCaret
                            clipTitle
                          >
                            <ul>
                              {titledDocuments.map((doc) => (
                                <m.li key={doc.id} layout="position">
                                  <div>
                                    <div style={{ minWidth: 70 }} />
                                    <DocumentRow
                                      creatorId={creatorId}
                                      compiledDocument={doc}
                                      onChangeTitle={handleTitleChange}
                                      onUploadComplete={d => handleEmptyRowManualUpload(doc, d)}
                                      fileNameOnly
                                      viewInModal={type === 'modal'}
                                      isViewOnly={!isInDashboard}
                                      viewAction={type === 'form-request' ? setDocumentOpen : undefined}
                                    />
                                    {isInDashboard && <>
                                      {doc.templateReferenceType === 'ai-bot' && (
                                        <Tooltip text="Generate with AI" classNameWrapper={s.tooltipWrapper} classNameTooltip={s.tooltip}>
                                          <button
                                            className={s['circle-button']}
                                            onClick={() => openUserWorkflowDocumentPane({ userWorkflowDocumentId: doc.id })}
                                          >
                                            <NoodleAI size={16} />
                                          </button>
                                        </Tooltip>
                                      )}
                                      <button className={s['circle-button']} onClick={() => handleRemoveItem(doc.id)}>
                                        <TrashSimple size={16} weight="fill" color="var(--color-error)" />
                                      </button>
                                    </>}
                                  </div>
                                </m.li>
                              ))}
                            </ul>
                          </CollapsibleArea>
                        </div>
                      ))}
                    </div>
                  )}
              </CollapsibleArea>
            </div>
          ))}
        </ul>
        {isInDashboard && <div className={s.sticky}>
          {showDownloadLink && type !== 'sidepanel' && (
            <DownloadArchive
              accessToken={accessToken}
              className={s['download-link']}
              documents={documentsWithPaths}
              userWorkflow={workflowData}
              isFoldered={true}
            />
          )}
          {type === 'sidepanel' && <>
            {showGeneratePdfsButton && <Button size="sm" variant='primary' loading={isGeneratingPdfs} onClick={handleGeneratePdfs}>Generate PDFs</Button>}
            {isPacerEnabled && <Button size="sm" variant='secondary' onClick={() => setPetitionBuilderModalOpen(true)}><Image src={PACERLogo} alt="Download" width={16} height={16} style={{ marginRight: 8 }} />ECF Manager</Button>}
            <Button size="sm" variant='secondary' onClick={() => openWorkflowDocumentsPane({ workflowDocumentsId: userWorkflowId })} className={s.fullScreenButton}><FrameCorners size={16} /></Button>
          </>}
        </div>}
      </div>
      {petitionBuilderModalOpen
        && <SelectPetitionPdfDocumentsModal
          creatorId={creatorId}
          onClose={() => setPetitionBuilderModalOpen(false)}
          onSubmit={() => {
            setPetitionBuilderModalOpen(false);
            refetchDocuments();
            openPacerPreviewPane({ userWorkflowId });
          }}
          userWorkflowId={userWorkflowId}
        />}
      {ecfManagerModalOpen
        && <ECFManagerModal
          documents={pacerDocs}
          onClose={() => setEcfManagerModalOpen(false)}
          userWorkflowId={userWorkflowId}
        />}
    </>
  );
};

export default UserWorkflowDocumentsWithFolders;
